class StickyAddToCart {
    constructor() {
      try {
        this.initializeElements();
      } catch (error) {
        console.error('Failed to initialize StickyAddToCart elements:', error);
        // Attempt recovery by retrying initialization after a delay
        setTimeout(() => this.initializeElements(), 1000);
      }
    }
  
    initializeElements() {
      try {
        this.stickyBar = document.getElementById("stickyAddToCart");
        this.mainAddToCartBtn = document.querySelector(".product-form__buttons");
        this.stickyForm = this.stickyBar?.querySelector('.sticky-add-to-cart__form');
        this.stickyAddToCartBtn =
          this.stickyBar?.querySelector('button[name="add"]');
        this.stickySelect = document.querySelector(
          ".sticky-add-to-cart__variant-selects select"
        );
        this.lastScrollTop = 0;
        this.isVariantSelected = false;
        this.mainVariantSelects = document.querySelector("variant-radios");
        this.currentSize = null;
        this.currentColor = null;
  
        // Check if critical elements exist
        if (!this.stickyBar || !this.mainAddToCartBtn) {
          throw new Error('Critical elements not found');
        }
  
        this.init();
      } catch (error) {
        console.error('Element initialization failed:', error);
        this.handleInitializationError();
      }
    }
  
    handleInitializationError() {
      // Try to recover by waiting for DOM to be fully loaded
      if (document.readyState !== 'complete') {
        window.addEventListener('load', () => {
          this.initializeElements();
        });
      } else {
        // If DOM is already loaded, try one more time after a delay
        setTimeout(() => this.initializeElements(), 2000);
      }
    }
  
    init() {
      try {
        // Bind methods with error handling
        this.handleScroll = this.safeHandleScroll.bind(this);
        this.handleStickySelect = this.safeHandleStickySelect.bind(this);
        this.checkVariantSelection = this.safeCheckVariantSelection.bind(this);
  
        // Add event listeners with error handling
        this.addEventListeners();
  
        // Initial setup
        this.safeSyncButtonStates();
        this.safeCheckVariantSelection();
        this.safeInitMainVariantListeners();
  
        // Add form submission handler
        if (this.stickyForm) {
          this.stickyForm.addEventListener('submit', (e) => this.handleSubmit(e));
        }
      } catch (error) {
        console.error('Initialization failed:', error);
      }
    }
  
    addEventListeners() {
      try {
        // Use passive scroll listener for better performance
        window.addEventListener("scroll", this.handleScroll, { passive: true });
        
        if (this.stickySelect) {
          this.stickySelect.addEventListener("change", this.handleStickySelect);
        }
  
        window.addEventListener("popstate", this.checkVariantSelection);
  
        // Add cleanup on page unload
        window.addEventListener('unload', () => {
          this.cleanup();
        });
      } catch (error) {
        console.error('Failed to add event listeners:', error);
      }
    }
  
    cleanup() {
      try {
        window.removeEventListener("scroll", this.handleScroll);
        this.stickySelect?.removeEventListener("change", this.handleStickySelect);
        window.removeEventListener("popstate", this.checkVariantSelection);
      } catch (error) {
        console.error('Cleanup failed:', error);
      }
    }
  
    safeHandleScroll() {
      try {
        if (!this.mainAddToCartBtn || !this.stickyBar) return;
  
        // Use requestAnimationFrame for smooth scrolling
        requestAnimationFrame(() => {
          const mainButton = this.mainAddToCartBtn.getBoundingClientRect();
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
  
          // Safari-specific scroll handling
          if (!/^((?!chrome|android).)*safari/i.test(navigator.userAgent)) {
            // Non-Safari browsers
            if (mainButton.top < 0) {
              if (scrollTop > this.lastScrollTop) {
                this.stickyBar.classList.add("is-visible");
              } else {
                this.stickyBar.classList.remove("is-visible");
              }
            } else {
              this.stickyBar.classList.remove("is-visible");
            }
          } else {
            // Safari-specific handling
            const shouldShow = mainButton.top < 0 && scrollTop > this.lastScrollTop;
            if (shouldShow) {
              requestAnimationFrame(() => {
                this.stickyBar.classList.add("is-visible");
              });
            } else {
              requestAnimationFrame(() => {
                this.stickyBar.classList.remove("is-visible");
              });
            }
          }
  
          this.lastScrollTop = scrollTop;
        });
      } catch (error) {
        console.error('Scroll handling failed:', error);
      }
    }
  
    safeHandleStickySelect(event) {
      try {
        if (!event || !event.target) return;
        
        const selectedVariantId = event.target.value;
        const variantRadios = document.querySelector("variant-radios");
        
        if (!variantRadios) return;
  
        this.updateVariantSelection(selectedVariantId, variantRadios);
      } catch (error) {
        console.error('Sticky select handling failed:', error);
      }
    }
  
    updateVariantSelection(variantId, variantRadios) {
      try {
        const productData = JSON.parse(
          document.querySelector('[data-json-type="product-variants"]')?.textContent || '{}'
        );
  
        const selectedVariant = productData.find(
          (variant) => variant.id.toString() === variantId
        );
  
        if (selectedVariant) {
          this.updateVariantUI(selectedVariant, variantRadios);
        }
      } catch (error) {
        console.error('Variant selection update failed:', error);
      }
    }
  
    updateVariantUI(variant, variantRadios) {
      try {
        const sizeLabel = document.querySelector(".product-detail__size-select-btn.select_btn");
        
        if (sizeLabel) {
          sizeLabel.textContent = variant.option2;
          sizeLabel.classList.remove("error");
        }
  
        // Update radio buttons with debounce
        this.debounce(() => {
          this.updateRadioButtons(variant, variantRadios);
        }, 100)();
      } catch (error) {
        console.error('Variant UI update failed:', error);
      }
    }
  
    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    }
  
    safeSyncButtonStates() {
      try {
        if (!this.stickyAddToCartBtn || !this.mainAddToCartBtn) return;
  
        const observer = new MutationObserver(this.debounce(() => {
          this.stickyAddToCartBtn.disabled = this.mainAddToCartBtn.disabled;
          const mainButtonText = this.mainAddToCartBtn.querySelector("span")?.textContent;
          const stickyButtonText = this.stickyAddToCartBtn.querySelector("span");
          if (stickyButtonText && mainButtonText) {
            stickyButtonText.textContent = mainButtonText;
          }
        }, 100));
  
        observer.observe(this.mainAddToCartBtn, {
          attributes: true,
          childList: true,
          subtree: true
        });
      } catch (error) {
        console.error('Button state sync failed:', error);
      }
    }
  
    safeCheckVariantSelection() {
      try {
        const queryParams = new URLSearchParams(window.location.search);
        this.isVariantSelected = !!queryParams.get("variant");
  
        if (!this.isVariantSelected) {
          this.updateStickyForSizeSelection();
        }
      } catch (error) {
        console.error('Variant selection check failed:', error);
      }
    }
  
    safeInitMainVariantListeners() {
      try {
        if (!this.mainVariantSelects) return;
  
        // Listen for changes on the main variant selectors
        this.mainVariantSelects.addEventListener("change", this.debounce((event) => {
          console.log('Main variant change:', {
            target: event.target.name,
            value: event.target.value
          });
  
          if (event.target.name === "Size") {
            this.currentSize = event.target.value;
          } else if (event.target.name === "Color" || event.target.name === "Colour") {
            this.currentColor = event.target.value;
          }
  
          // Always update sticky select when either size or color changes
          this.updateStickySelect();
        }, 100));
  
        // Initialize current selections
        this.initializeCurrentSelections();
      } catch (error) {
        console.error('Main variant listeners initialization failed:', error);
      }
    }
  
    initializeCurrentSelections() {
      try {
        // Get initial selections
        const selectedSize = this.mainVariantSelects.querySelector('input[name="Size"]:checked');
        const selectedColor = this.mainVariantSelects.querySelector('input[name="Color"]:checked, input[name="Colour"]:checked');
  
        console.log('Initial selections:', {
          size: selectedSize?.value,
          color: selectedColor?.value
        });
  
        if (selectedSize) this.currentSize = selectedSize.value;
        if (selectedColor) this.currentColor = selectedColor.value;
  
        // Initial sync with sticky select if we have both values
        if (this.currentSize && this.currentColor) {
          this.updateStickySelect();
        }
      } catch (error) {
        console.error('Current selections initialization failed:', error);
      }
    }
  
    updateStickyForSizeSelection() {
      // Update select element
      const stickySelect = document.querySelector(
        ".sticky-add-to-cart__variant-selects select"
      );
      if (stickySelect) {
        stickySelect.value = "";
  
        // Add "Select a size" option if it doesn't exist
        if (!stickySelect.querySelector('option[value=""]')) {
          const selectSizeOption = document.createElement("option");
          selectSizeOption.value = "";
          selectSizeOption.textContent = "Select a size";
          selectSizeOption.selected = true;
          stickySelect.insertBefore(selectSizeOption, stickySelect.firstChild);
        }
      }
  
      // Update button text
      const stickyButton = document.querySelector(
        ".sticky-add-to-cart__button button"
      );
      if (stickyButton) {
        const buttonSpan = stickyButton.querySelector("span") || stickyButton;
        buttonSpan.textContent = "Select a size";
        stickyButton.disabled = true;
      }
    }
  
    updateRadioButtons(variant, variantRadios) {
      try {
        // Update color selection
        const colorInputs = variantRadios.querySelectorAll('input[name="Color"], input[name="Colour"]');
        colorInputs.forEach((input) => {
          if (input.value === variant.option1) {
            input.click();
          }
        });
  
        // Update size selection
        const sizeInputs = variantRadios.querySelectorAll('input[name="Size"]');
        sizeInputs.forEach((input) => {
          if (input.value === variant.option2) {
            input.click();
          }
        });
      } catch (error) {
        console.error('Radio button update failed:', error);
      }
    }
  
    updateStickySelect() {
      try {
        if (!this.stickySelect) return;
  
        console.group('Sticky Select Update');
        console.log('Current selections:', {
          size: this.currentSize,
          color: this.currentColor,
          allOptions: Array.from(this.stickySelect.options).map(opt => opt.textContent.trim())
        });
  
        // Get the currently selected color from the main variant selectors
        const selectedColorInput = this.mainVariantSelects.querySelector('input[name="Color"]:checked, input[name="Colour"]:checked');
        const selectedColor = selectedColorInput ? selectedColorInput.value : this.currentColor;
  
        console.log('Selected color from main variant:', selectedColor);
  
        // Find the option that matches both size and color
        const options = Array.from(this.stickySelect.options);
        const matchingOption = options.find((option) => {
          const optionText = option.textContent.trim();
          // Split the option text into color and size
          const [optionColor, optionSize] = optionText.split('/').map(part => part.trim());
          
          const hasSize = optionSize === this.currentSize;
          const hasColor = optionColor === selectedColor;
  
          console.log('Checking option:', {
            optionText,
            optionColor,
            optionSize,
            currentSize: this.currentSize,
            selectedColor,
            hasSize,
            hasColor
          });
  
          return hasSize && hasColor;
        });
  
        if (matchingOption) {
          console.log('Found matching option:', matchingOption.textContent.trim());
          this.stickySelect.value = matchingOption.value;
          
          // Dispatch change event to ensure any listeners are triggered
          const event = new Event('change', { bubbles: true });
          this.stickySelect.dispatchEvent(event);
        } else {
          console.warn('No matching option found for:', {
            size: this.currentSize,
            color: selectedColor
          });
        }
        console.groupEnd();
      } catch (error) {
        console.error('Sticky select update failed:', error);
      }
    }
  
    async handleSubmit(event) {
      event.preventDefault();
  
      if (!this.stickyForm || !this.stickyAddToCartBtn) return;
  
      try {
        console.group('Add to Cart Process');
        console.log('Starting add to cart submission');
        
        // Show loading state
        this.stickyAddToCartBtn.setAttribute('aria-busy', 'true');
        this.stickyAddToCartBtn.disabled = true;
        console.log('Loading state activated');
  
        const formData = new FormData(this.stickyForm);
        console.log('Form Data:', Object.fromEntries(formData));
        
        console.log('Sending request to cart/add.js');
        const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          body: formData
        });
  
        const data = await response.json();
        console.log('Cart Response:', data);
  
        if (response.ok) {
          console.log('Successfully added to cart');
          this.updateCartDrawer(data);
        } else {
          throw new Error(data.description || 'Error adding to cart');
        }
      } catch (error) {
        console.error('Add to cart failed:', {
          error,
          message: error.message,
          stack: error.stack
        });
        // Show error message to user
        this.showError(error.message);
      } finally {
        // Reset button state
        this.stickyAddToCartBtn.removeAttribute('aria-busy');
        this.stickyAddToCartBtn.disabled = false;
        console.log('Reset button state');
        console.groupEnd();
      }
    }
  
    async updateCartDrawer(cartData) {
      try {
        console.group('Cart Drawer Update');
        console.log('Cart Data:', cartData);
        console.log('Is iOS Device:', this.isIOS());
  
        // Wait a moment for the cart to update
        await new Promise(resolve => setTimeout(resolve, 100));
  
        if (this.isIOS()) {
          console.log('Attempting to show Rebuy Smart Cart on iOS');
          try {
            document.dispatchEvent(
              new CustomEvent('rebuy:smartcart.show', {
                detail: { smartcart: cartData }
              })
            );
            console.log('Rebuy Smart Cart event dispatched successfully');
          } catch (iosError) {
            console.error('Failed to show Rebuy Smart Cart:', iosError);
          }
        } else {
          console.log('Attempting to open default cart drawer');
          const cartDrawerTrigger = document.querySelector('cart-drawer summary');
          console.log('Cart Drawer Trigger found:', !!cartDrawerTrigger);
          
          if (cartDrawerTrigger) {
            cartDrawerTrigger.click();
            console.log('Cart Drawer click triggered');
          } else {
            console.warn('Cart Drawer trigger element not found');
          }
        }
        console.groupEnd();
      } catch (error) {
        console.error('Cart drawer update failed:', {
          error,
          errorMessage: error.message,
          stack: error.stack
        });
      }
    }
  
    // Helper method to detect iOS with detailed logging
    isIOS() {
      const devices = [
        'iPad Simulator',
        'iPhone Simulator',
        'iPod Simulator',
        'iPad',
        'iPhone',
        'iPod'
      ];
      
      const isPlatformIOS = devices.includes(navigator.platform);
      const isMacWithTouch = navigator.userAgent.includes("Mac") && "ontouchend" in document;
      
      console.log('iOS Detection:', {
        platform: navigator.platform,
        userAgent: navigator.userAgent,
        isPlatformIOS,
        isMacWithTouch,
        hasTouch: "ontouchend" in document
      });
      
      return isPlatformIOS || isMacWithTouch;
    }
  
    showError(message) {
      // Create error message element
      const errorContainer = document.createElement('div');
      errorContainer.classList.add('sticky-cart-error');
      errorContainer.textContent = message;
      
      // Add error message to sticky cart
      this.stickyForm.appendChild(errorContainer);
      
      // Remove error after 3 seconds
      setTimeout(() => {
        errorContainer.remove();
      }, 3000);
    }
  }
  
  // Initialize with retry mechanism
  let initAttempts = 0;
  const maxAttempts = 3;
  
  function initializeStickyAddToCart() {
    try {
      new StickyAddToCart();
    } catch (error) {
      console.error(`Initialization attempt ${initAttempts + 1} failed:`, error);
      if (initAttempts < maxAttempts) {
        initAttempts++;
        setTimeout(initializeStickyAddToCart, 1000 * initAttempts);
      }
    }
  }
  
  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeStickyAddToCart);
  } else {
    initializeStickyAddToCart();
  }
  